import { Suspense, lazy } from "react";
import { Route, Routes } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import Flx from "../../components/layout/Flx";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";

// Adapted from digital_sizer src/admin/AdminApp.tsx — the correspondents
// sub-app shell: nested routes for List / Coverage Matrix / Import / New /
// Drilldown. Mounted at /correspondents/* in index.js.
//
// List and Coverage Matrix resolve to the SAME screen — one table, two column
// sets, chosen by the selector under the page title. The old
// `pages/CoverageMatrix.js` is superseded and no longer routed.
const CorrespondentsList = lazy(() => import("./pages/List"));
const CorrespondentDrilldown = lazy(() => import("./pages/Drilldown"));
const CorrespondentImport = lazy(() => import("./pages/Import"));
const CorrespondentNew = lazy(() => import("./pages/New"));

const Fallback = () => (
  <Flx center sx={{ minHeight: 200, py: 4 }}>
    <CircularProgress size={24} />
  </Flx>
);

// No sub-nav any more: List and Coverage Matrix are ONE table with two column
// sets, switched by the selector under the page title. A top bar whose only job
// was choosing between them is now a second control for the same thing.
// `/correspondents/coverage-matrix` still resolves (old links, bookmarks) — it
// renders the same screen with the coverage view preselected. "Import xlsx"
// remains unlinked: no backend route, 404s on every click.

const CorrespondentsScreen = () => {
  useDocumentTitle("Correspondents");

  return (
    <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height: "100%",
      minHeight: 0,
      overflow: "hidden",
      bgcolor: "#fff",
    }}
  >
    <Box
      sx={{
        flex: "1 1 0",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        // The screen root is `height:100%; overflow:hidden` so the List page's
        // grid can own its own scrolling. Without a scroll container here, any
        // routed page that grows TALLER than the viewport — the drilldown with
        // several funding entities, a long Pricing tab — was simply clipped
        // with no way to reach the rest. `auto` is a no-op for the pages that
        // already fit.
        overflowY: "auto",
        // The containing block for anything absolutely positioned inside a
        // routed page. Without it that block is the dashboard <main>, which is
        // positioned and scrolls too — so an absolute element deep in a tall
        // drilldown tab stretched <main> past this screen, leaving a white
        // band below the page that <main> could be scrolled into.
        position: "relative",
      }}
    >
      <Suspense fallback={<Fallback />}>
        <Routes>
          <Route index element={<CorrespondentsList />} />
          <Route path="new" element={<CorrespondentNew />} />
          <Route
            path="coverage-matrix"
            element={<CorrespondentsList initialView="coverage" />}
          />
          <Route path="import" element={<CorrespondentImport />} />
          <Route path=":id" element={<CorrespondentDrilldown />} />
        </Routes>
      </Suspense>
      </Box>
    </Box>
  );
};

export default CorrespondentsScreen;
